import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHead from "@/components/PageHead";
import { renderPostBody, autoExcerpt } from "@/lib/markdown";
import { sanitizeHtml } from "@/lib/sanitize";
import { useGetStarted } from "@/components/GetStartedDialog";
import NewsletterSignup from "@/components/NewsletterSignup";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound | error
  const { open } = useGetStarted();

  useEffect(() => {
    let mounted = true;
    setStatus("loading");
    api
      .get(`/journal/${slug}`)
      .then((r) => {
        if (!mounted) return;
        setPost(r.data);
        setStatus("ready");
      })
      .catch((e) => {
        if (!mounted) return;
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 py-24" data-testid="blog-post-loading">
        <p className="font-body text-ink-soft">Loading entry…</p>
      </section>
    );
  }

  if (status === "notfound") {
    return (
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 py-24" data-testid="blog-post-notfound">
        <p className="eyebrow">§ Blog · 404</p>
        <h1 className="font-display text-5xl md:text-7xl mt-6">
          That entry <em className="not-italic text-brand-red">isn&rsquo;t here.</em>
        </h1>
        <p className="mt-6 max-w-prose text-lg text-ink-soft">
          It may have been unpublished or renamed. Head back to the blog to
          find something else worth reading.
        </p>
        <Link
          to="/blog"
          className="mt-8 inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
        >
          Back to the blog <span aria-hidden>→</span>
        </Link>
      </section>
    );
  }

  if (status === "error" || !post) {
    return (
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 py-24" data-testid="blog-post-error">
        <p className="eyebrow">Blog unavailable</p>
        <h1 className="font-display text-4xl md:text-5xl mt-4">
          We couldn&rsquo;t load that entry.
        </h1>
        <p className="mt-4 max-w-prose text-ink-soft">Please try again in a moment.</p>
      </section>
    );
  }

  const html = renderPostBody(post.body);
  const desc = post.excerpt || autoExcerpt(post.body);
  const publishedIso = post.published_at || post.created_at;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: desc,
    author: { "@type": "Person", name: post.author || "Jeremy Redkey" },
    datePublished: publishedIso,
    dateModified: post.updated_at || publishedIso,
    image: post.cover_image || undefined,
    mainEntityOfPage: `https://redkeyweb.design/blog/${post.slug}`,
    publisher: {
      "@type": "Organization",
      name: "Redkey Web Design",
      logo: {
        "@type": "ImageObject",
        url: "https://redkeyweb.design/brand/redkey-logo.png",
      },
    },
    keywords: (post.keywords || []).join(", "),
  };

  return (
    <div data-testid="blog-post-page">
      <PageHead
        title={`${post.title} — Redkey Blog`}
        description={desc}
        canonical={`https://redkeyweb.design/blog/${post.slug}`}
        jsonLd={jsonLd}
      />

      {/* Header */}
      <section className="mx-auto max-w-[900px] px-6 md:px-10 pt-16 md:pt-24 pb-8">
        <Link
          to="/blog"
          className="eyebrow link-underline-grow"
          data-testid="blog-post-back"
        >
          ← Back to blog
        </Link>
        <div className="mt-6 flex flex-wrap items-baseline gap-3">
          <p className="eyebrow">{formatDate(publishedIso)}</p>
          {post.keywords && post.keywords.length > 0 && (
            <p className="font-ui text-[11px] text-label tracking-widest uppercase">
              {post.keywords.slice(0, 4).join(" · ")}
            </p>
          )}
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.02]"
          data-testid="blog-post-title"
        >
          {post.title}
        </h1>
        {desc && (
          <p className="mt-6 text-lg md:text-xl text-ink-soft max-w-prose leading-relaxed">
            {desc}
          </p>
        )}
        <p className="mt-6 font-ui text-sm text-label">
          By {post.author || "Jeremy Redkey"}
        </p>
      </section>

      {/* Cover */}
      {post.cover_image && (
        <section className="mx-auto max-w-[1200px] px-6 md:px-10 pb-10">
          <div className="aspect-[16/9] overflow-hidden bg-paper-alt border hairline">
            <img
              src={post.cover_image}
              alt={`${post.title} — cover`}
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        </section>
      )}

      {/* Body */}
      <section className="mx-auto max-w-[720px] px-6 md:px-10 pb-16">
        <article
          data-testid="blog-post-body"
          className="prose-redkey"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
        />
      </section>

      {/* Newsletter — subscribe from the reader */}
      <section className="mx-auto max-w-[720px] px-6 md:px-10 pb-20">
        <NewsletterSignup variant="compact" surface="alt" />
      </section>

      {/* CTA */}
      <section className="border-t hairline bg-paper-alt">
        <div className="mx-auto max-w-[900px] px-6 md:px-10 py-16 md:py-24">
          <p className="eyebrow">§ Work with us</p>
          <h2 className="font-display text-3xl md:text-5xl mt-4 max-w-[24ch]">
            Building something similar? Let&rsquo;s make it{" "}
            <em className="not-italic text-brand-red">real.</em>
          </h2>
          <button
            type="button"
            onClick={open}
            data-testid="blog-post-cta"
            className="mt-8 inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
          >
            Get Started <span aria-hidden>→</span>
          </button>
        </div>
      </section>
    </div>
  );
}
